import makeStyles from '@mui/styles/makeStyles';
import { ReactElement, useEffect } from 'react';
import { RaptorTheme } from '../../../../../../styling/theme';
import usePngFromComponent from '../../../../../../hooks/usePngFromComponent';
import { useDispatch } from 'react-redux';
import { addComponentToPdfExport } from '../../../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../../../types/redux/pdfExports/FileExportsStore';
import { Typography } from '@mui/material';
import {
  type EmirBreakdownPayload,
} from '../../../../../../api/rs-hg/emir';
import MancoSectionSummariesPies from '../MancoSectionSummariesPies/MancoSectionSummariesPies.component';

const useMancoSummaryContainerStyles = makeStyles((theme: RaptorTheme) => ({
  regulationContainerRoot: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '0.62rem 1.88rem',
    paddingRight: 0,
    transition: 'all .3s',
    height: '100%',
    width: '100%',
    zIndex: 1,
    position: 'relative',
    cursor: 'pointer',
  },
  textSection: {
    display: 'flex',
    borderRadius: 4,
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '25%',
  },
  subTitle: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
  },
  subTitle_info: {
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.12rem',
    },
    fontSize: '1.50rem',
    fontWeight: 'bold',
    marginBottom: '0.94rem',
    color: theme.palette.primary.main,
  },
  pieSection: {
    cursor: 'pointer',
    display: 'flex',
    borderRadius: 4,
    width: '75%',
    justifyContent: 'space-around',
  },
}));

interface MancoSummaryProps {
  payload: EmirBreakdownPayload;
  fundsCount: number;
  returnedPositionDate: string | null;
  selectedKey: string;
  setSelectedKeyFunction: (key: string) => void;
}

function MancoSummaryContainer({
  payload,
  fundsCount,
  returnedPositionDate,
  selectedKey,
  setSelectedKeyFunction,
}: MancoSummaryProps): ReactElement {
  const classes = useMancoSummaryContainerStyles();
  const numberSubFunds = fundsCount;

  const dispatch = useDispatch();
  const { ref, handleDownload } = usePngFromComponent();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: 'emir-manco-summary',
        handler: handleDownload,
        type: PdfComponentType.GENERAL_COMPONENT_IMAGE,
        title: '',
        dontMoveToNewPage: true,
        specificYPosition: undefined,
        specificXPosition: undefined,
        alignment: undefined,
        dontIncrementYPosition: false,
        groupOrder: 0,
      }),
    );
  }, [payload, ref]);

  return (
    <div className={classes.regulationContainerRoot} ref={ref}>
      <div className={classes.textSection}>
        <Typography variant="h1" style={{ borderBottom: '0.62rem' }}>
          {'Manco Summary'}
        </Typography>
        <div>
          <Typography variant="h2" className={classes.subTitle}>
            Number of Sub-Funds:
          </Typography>
          <Typography variant="h3" className={classes.subTitle_info}>
            {numberSubFunds}
          </Typography>
          {returnedPositionDate && (
            <>
              <Typography variant="h2" className={classes.subTitle}>
                Position Date:
              </Typography>
              <Typography variant="h3" className={classes.subTitle_info}>
                {returnedPositionDate}
              </Typography>
            </>
          )}
        </div>
      </div>
      <div className={classes.pieSection}>
        <MancoSectionSummariesPies
          sections={payload.manco_section_summaries}
          sectionOrder={
            'manco_limits' in payload && payload.manco_limits
              ? Object.keys(payload.manco_limits)
              : undefined
          }
          selectedKey={selectedKey}
          setSelectedKeyFunction={setSelectedKeyFunction}
        />
      </div>
    </div>
  );
}

export default MancoSummaryContainer;
